import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authFetchJson } from '../lib/authFetch'
import type { User, UserGroup } from '../../../api/src/db/schema/auth'

interface UserRow extends User {
  group?: UserGroup
}

interface UsersListResponse {
  users: UserRow[]
}

interface GroupsListResponse {
  groups: UserGroup[]
}

interface InviteFormData {
  name: string
  email: string
  role: string
  group_id: number | null
}

export function AdminUsersPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Check permission
  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-h1 text-text-primary mb-2">403 — Access Denied</h1>
          <p className="text-body text-text-secondary mb-6">
            You do not have permission to access this page.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-red text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const [users, setUsers] = useState<UserRow[]>([])
  const [groups, setGroups] = useState<UserGroup[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteForm, setInviteForm] = useState<InviteFormData>({
    name: '',
    email: '',
    role: 'VIEWER',
    group_id: null,
  })
  const [inviteSaving, setInviteSaving] = useState(false)
  const [editingUserId, setEditingUserId] = useState<number | null>(null)
  const [editingRole, setEditingRole] = useState('VIEWER')
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null)

  const roleOptions = ['VIEWER', 'EDITOR', 'ADMIN', 'SUPER_ADMIN', 'SCOUT']

  // Load users and groups
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const [usersData, groupsData] = await Promise.all([
          authFetchJson<UsersListResponse>('/api/v2/admin/users'),
          authFetchJson<GroupsListResponse>('/api/v2/admin/user-groups'),
        ])
        setUsers(usersData.users || [])
        setGroups(groupsData.groups || [])
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteForm.name || !inviteForm.email) return

    try {
      setInviteSaving(true)
      const newUser = await authFetchJson<UserRow>('/api/v2/admin/users', {
        method: 'POST',
        json: {
          name: inviteForm.name,
          email: inviteForm.email,
          role: inviteForm.role,
          group_id: inviteForm.group_id,
        },
      })
      setUsers([...users, newUser])
      setShowInviteModal(false)
      setInviteForm({ name: '', email: '', role: 'VIEWER', group_id: null })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user')
    } finally {
      setInviteSaving(false)
    }
  }

  const handleUserUpdate = async (userId: number) => {
    try {
      const updated = await authFetchJson<UserRow>(
        `/api/v2/admin/users/${userId}`,
        {
          method: 'PATCH',
          json: {
            role: editingRole,
            group_id: editingGroupId,
          },
        }
      )
      setUsers(users.map((u) => (u.id === userId ? updated : u)))
      setEditingUserId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    }
  }

  const handleToggleActive = async (userId: number, currentActive: boolean | null) => {
    try {
      const updated = await authFetchJson<UserRow>(
        `/api/v2/admin/users/${userId}`,
        {
          method: 'PATCH',
          json: { is_active: !currentActive },
        }
      )
      setUsers(users.map((u) => (u.id === userId ? updated : u)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    }
  }

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
          <span className="text-body text-text-secondary">Loading…</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border-subtle bg-bg-surface p-6 flex justify-between items-start">
        <div>
          <h1 className="text-h1 text-text-primary mb-2">Admin — Users</h1>
          <p className="text-body text-text-secondary">
            Manage platform users, roles, and groups
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-4 py-2 bg-primary-red text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Invite User
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-bg-body">
        {error && (
          <div className="m-6 p-4 rounded-md bg-red-400/10 border border-red-400/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {users.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-body text-text-secondary">No users found</p>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="overflow-x-auto rounded-lg border border-border-subtle">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-subtle bg-bg-surface">
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">
                      Group
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">
                      Last Login
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border-subtle hover:bg-bg-surface">
                      <td className="px-4 py-3">
                        <p className="text-body font-medium text-text-primary">
                          {u.name || '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-body text-text-secondary">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {editingUserId === u.id ? (
                          <select
                            value={editingRole}
                            onChange={(e) => setEditingRole(e.target.value)}
                            className="px-2 py-1 bg-bg-body border border-border-subtle rounded text-sm text-text-primary"
                          >
                            {roleOptions.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-bg-surface border border-border-subtle text-text-secondary">
                            {u.role}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingUserId === u.id ? (
                          <select
                            value={editingGroupId || ''}
                            onChange={(e) =>
                              setEditingGroupId(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            className="px-2 py-1 bg-bg-body border border-border-subtle rounded text-sm text-text-primary"
                          >
                            <option value="">None</option>
                            {groups.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-body text-text-secondary">
                            {u.group?.name || '—'}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleActive(u.id, u.is_active)}
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            u.is_active
                              ? 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20'
                              : 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
                          }`}
                        >
                          {u.is_active ? 'Active' : 'Disabled'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-text-secondary">
                          {u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {editingUserId === u.id ? (
                            <>
                              <button
                                onClick={() => handleUserUpdate(u.id)}
                                className="px-2 py-1 bg-primary-red text-white rounded text-xs hover:bg-red-700"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingUserId(null)}
                                className="px-2 py-1 bg-bg-body border border-border-subtle rounded text-xs"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingUserId(u.id)
                                  setEditingRole(u.role || 'VIEWER')
                                  setEditingGroupId(u.group_id)
                                }}
                                className="px-2 py-1 bg-bg-surface border border-border-subtle rounded text-xs hover:bg-border-subtle"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleCopyEmail(u.email)}
                                className="px-2 py-1 bg-bg-surface border border-border-subtle rounded text-xs hover:bg-border-subtle"
                                title="Copy email"
                              >
                                📋
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-bg-surface rounded-lg border border-border-subtle p-6 max-w-md w-full mx-4">
            <h2 className="text-h2 text-text-primary mb-4">Invite User</h2>
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-text-tertiary uppercase mb-1">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={inviteForm.name}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-bg-body border border-border-subtle rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-text-tertiary uppercase mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-bg-body border border-border-subtle rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-text-tertiary uppercase mb-1">
                  Role
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, role: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-bg-body border border-border-subtle rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                >
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-text-tertiary uppercase mb-1">
                  Group (Optional)
                </label>
                <select
                  value={inviteForm.group_id || ''}
                  onChange={(e) =>
                    setInviteForm({
                      ...inviteForm,
                      group_id: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  className="w-full px-3 py-2 bg-bg-body border border-border-subtle rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                >
                  <option value="">None</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={inviteSaving}
                  className="flex-1 px-4 py-2 bg-primary-red text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
                >
                  {inviteSaving ? 'Sending...' : 'Invite User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 bg-bg-body border border-border-subtle rounded-md hover:bg-border-subtle transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
